import type { BtlApplication } from 'api/btlApplicationForm/types';
import { App } from 'antd';
import { firmApi } from 'api/firm/firm.api';
import { userApi } from 'api/user';
import {
  getAdjacentOoCaseSidebarItemId,
  getAdjacentOoCaseSidebarUrlSection,
  getOoCaseSidebarNavigationLabel,
} from 'components/CaseSidebar';
import type { OoCaseSidebarContext } from 'components/CaseSidebar/buildOoCaseSidebar';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import { FormValidationContext } from 'hooks';
import { propertyFormSchema } from 'pages/BtlApplicationFormPage/FinancingAndProperty/formSchema';
import { hydrateFinancingAndPropertyFormValues } from 'pages/BtlApplicationFormPage/FinancingAndProperty/mapApplicationToFormValues';
import { submitFinancingAndPropertyForm } from 'pages/BtlApplicationFormPage/FinancingAndProperty/submitFinancingAndPropertyForm';
import { makeFormReadonly } from 'pages/BtlApplicationFormPage/makeFormReadonly';
import { useBtlApplicationInProgressById } from 'queries';
import { useBrokerFirm } from 'queries/broker/useBrokerFirm';
import { useAuth } from 'providers';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface UseOoCasePropertyFormParams {
  caseId: string;
  sidebarContext: OoCaseSidebarContext;
}

export function isOoPropertyFormEditable(caseData?: BtlApplication): boolean {
  return caseData?.status === 'DIP_ACCEPTED';
}

function getPropertyFormParams(caseData?: BtlApplication, caseId?: string) {
  const property = caseData?.applicationProperty?.property;

  return {
    caseId,
    propertyId: property?.id,
    status: caseData?.status,
    tenure: caseData?.applicationProperty?.tenure,
    purchasePrice: property?.purchasePrice,
    propertyType: property?.propertyType,
    postcode: property?.address?.postcode,
  };
}

export function useOoCasePropertyForm({ caseId, sidebarContext }: UseOoCasePropertyFormParams) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { formatMessage } = useIntl();
  const { isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const { data: caseData, isLoading: isCaseLoading } = useBtlApplicationInProgressById(caseId);
  const { data: firm, isLoading: isFirmLoading } = useBrokerFirm();
  const queryClient = useQueryClient();
  const formValidationContext = useContext(FormValidationContext);
  const formSectionParam = searchParams.get('section') ?? CaseSidebarItemId.PROPERTY;
  const propertyId = caseData?.applicationProperty?.property?.id;
  const isFormEditable = isOoPropertyFormEditable(caseData);
  const nextSection = getAdjacentOoCaseSidebarUrlSection(formSectionParam, sidebarContext, 'next') ?? '';
  const formParams = useMemo(() => getPropertyFormParams(caseData, caseId), [caseData, caseId]);
  const formKey = useMemo(() => JSON.stringify(formParams), [formParams]);

  if (!formValidationContext) {
    throw new Error('OO property form must be used within a FormValidationProvider');
  }

  const { setIsSubmitting } = formValidationContext;

  if (isCaseLoading || isFirmLoading) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    firm,
    isFormEditable,
    isDipAcceptedOrLater: true,
    variant: 'oo' as const,
  };

  const formSchema = propertyFormSchema(schemaProps);
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return {
    isLoading: false as const,
    isFormEditable,
    formKey,
    footerProps: {
      previousSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'previous'),
      nextSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'next'),
      previousLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'previous'),
      nextLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'next'),
    },
    formProps: {
      columns: adjustedFormSchema,
      isFormEditable,
      name: 'oo_property',
      params: formParams,
      request: async () => {
        let brokerReferrerId: string | undefined;

        if (isSignedIn) {
          try {
            const user = await userApi.getUser();
            if (user?.id) {
              const brokerReferrer = await firmApi.getBrokerReferrerByUserId(user.id);
              brokerReferrerId = brokerReferrer.id;
            }
          } catch {
            brokerReferrerId = undefined;
          }
        }

        return hydrateFinancingAndPropertyFormValues(caseData, firm, brokerReferrerId);
      },
      onFinish: async (values: Record<string, unknown>) => {
        await submitFinancingAndPropertyForm({
          values,
          data: caseData,
          firm,
          id: caseId,
          propertyId,
          nextSection,
          navigate: () => undefined,
          onNavigateAfterSubmit: section => navigate({ search: `?section=${section}` }),
          queryClient,
          setIsSubmitting,
          message,
          showSuccessMessage: false,
        });
      },
    },
  };
}
